import { HOST_STATES, type CloudLoginState, type HostState, type RunDTO, type SandboxStatusDTO } from "@roomate/core";

/**
 * Remote runtime, agent page (docs/specs/remote-runtime.md §11 T9).
 * Pure helpers: the components only render what these return.
 */

export type { SandboxStatusDTO };
/** The hub's own list of sandbox states (GET /actors/:id/sandbox). */
export const SANDBOX_STATES = HOST_STATES;
export type SandboxState = HostState;
export type SandboxLogin = CloudLoginState;

export const SANDBOX_STATE_LABEL: Record<SandboxState, string> = {
  pending: "Starting",
  running: "Running",
  stopping: "Stopping",
  stopped: "Stopped",
  failed: "Failed",
};

export const sandboxStateLabel = (state: SandboxState | null): string => (state ? SANDBOX_STATE_LABEL[state] : "Not created yet");

export const LOGIN_LABEL: Record<SandboxLogin, string> = { ok: "Logged in", missing: "Not logged in", expired: "Login expired" };

export const loginLabel = (login: SandboxLogin | null): string => (login ? LOGIN_LABEL[login] : "Login unknown");

/** Same string as `CloudHost.connectCommand` (vercel-host.ts). Null until the sandbox has a stored name. */
export const connectCommand = (sandboxName: string | null): string | null => (sandboxName ? `sandbox connect ${sandboxName}` : null);

/** The owner must run the connect command again: the sandbox has no usable Claude login. */
export const needsReconnect = (status: Pick<SandboxStatusDTO, "loginState"> | null | undefined): boolean => status?.loginState === "missing" || status?.loginState === "expired";

/**
 * The hub learns a sandbox's login only from an explicit check, so a running sandbox whose login is unknown
 * (or not ok) offers one. The hub refuses a check on a sandbox that is not running.
 */
export const canCheckLogin = (status: Pick<SandboxStatusDTO, "state" | "loginState">): boolean => status.state === "running" && status.loginState !== "ok";

export const RECONNECT_TITLE = "Re-connect this agent's sandbox";

/** Resume is offered only for a sandbox that exists and is not already coming up. */
export const canResume = (state: SandboxState | null): boolean => state === "stopped" || state === "failed";

export type StopPhase = "live" | "stopping" | "stopped" | "ended";

/** The agent's live remote run, if any. A run on a node is not stopped from this panel's sandbox controls. */
export const liveRemoteRun = (runs: readonly RunDTO[]): RunDTO | null =>
  runs.find((r) => r.host === "vercel-sandbox" && (r.status === "starting" || r.status === "running")) ?? null;

/** The run the panel reports on: the live remote run, else the newest remote run if a person just stopped it. */
export function panelRun(runs: readonly RunDTO[], now: number = Date.now()): RunDTO | null {
  const live = liveRemoteRun(runs);
  if (live) return live;
  const newest = runs.find((r) => r.host === "vercel-sandbox");
  const recent = newest?.finishedAt != null && now - newest.finishedAt < STOPPED_VISIBLE_MS;
  // A stop request that lost the race with the run's own end still resolves visibly (as "ended").
  return newest && (newest.status === "stopped" || newest.stopRequested) && recent ? newest : null;
}

/** How long "Run stopped" stays on the card after the run ended. */
export const STOPPED_VISIBLE_MS = 5 * 60_000;

/** live → (stop recorded) stopping → stopped, from the run row alone. */
export function stopPhase(run: Pick<RunDTO, "status" | "stopRequested">): StopPhase {
  if (run.status === "stopped") return "stopped";
  if (run.status !== "starting" && run.status !== "running") return "ended";
  return run.stopRequested ? "stopping" : "live";
}

/** Whole-unit age: "just now", "5 min ago", "3 h ago", "2 d ago". */
export function snapshotAge(at: number | null, now: number): string {
  if (at === null) return "no snapshot yet";
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}
